import { DocumentsApiService, ExistingDocument } from '@abb-procure/data';
import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '../../pipes/date.pipe';
import { FileExtensionPipe } from '../../pipes/file-extension.pipe';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { NgChanges } from '../../utilities/angular-types.util';
import { Button } from '../button/button.component';
import { ExpansionPanel } from '../expansion-panel/expansion-panel.component';
import { FileIcon } from '../file-icon/file-icon.component';

@Component({
  selector: 'procure-post-attachments',
  templateUrl: './post-attachments.component.html',
  styleUrl: './post-attachments.component.scss',
  imports: [
    ExpansionPanel,
    MatTable,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderCellDef,
    MatCell,
    MatRow,
    MatCellDef,
    MatColumnDef,
    MatFooterRowDef,
    MatFooterRow,
    MatFooterCell,
    MatFooterCellDef,
    MatRowDef,
    MatHeaderRowDef,
    FileIcon,
    MatTooltip,
    Button,
    MatIcon,
    DatePipe,
    FileSizePipe,
    FileExtensionPipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class PostAttachments implements OnChanges {
  @Input() documents: ExistingDocument[] = [];

  protected dataSource: MatTableDataSource<ExistingDocument> =
    new MatTableDataSource<ExistingDocument>([]);

  protected displayedColumns: string[] = [
    'fileName',
    'fileUploadDate',
    'fileSize',
    'actions',
  ];

  protected combinedFileSize: number = 0;
  protected isDownloading: boolean = false;

  private currentDownloads: ExistingDocument[] = [];
  private readonly documentsApiService = inject(DocumentsApiService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnChanges(changes: NgChanges<PostAttachments>): void {
    if (changes.documents) {
      this.updateDataSource(this.documents);
    }
  }

  getUserName(doc: ExistingDocument): string {
    if (doc.uploadBy) {
      return doc.uploadBy.name != null
        ? `Uploaded By: ${doc.uploadBy.name}`
        : 'unknown';
    }

    if (doc.downloadError) {
      return 'There was an error when generating the download url';
    }

    return '';
  }

  isCurrentDocumentDownloading(document?: ExistingDocument): boolean {
    return this.currentDownloads.find((el) => document === el) !== undefined;
  }

  async downloadSingleDocument(file: ExistingDocument): Promise<void> {
    this.isDownloading = true;
    this.currentDownloads = [...this.currentDownloads, file];

    file.downloadUrl = '';

    const response = await this.documentsApiService.getDownloadUrl(file.id);

    this.isDownloading = false;
    this.currentDownloads = this.currentDownloads.filter((el) => el !== file);

    if (response) {
      file.downloadUrl = response;
      file.downloadError = false;

      window.location.href = response;
    } else {
      file.downloadError = true;
    }
  }

  private updateDataSource(documents: ExistingDocument[]): void {
    this.dataSource.data = documents;
    this.combinedFileSize = documents.reduce((a, b) => a + b.fileSize, 0);
  }
}
